import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchServiceProfiles } from '@/services/serviceProfiles';
import { DataTable, type Column } from '@/components/DataTable';
import { ServiceForm } from '@/components/ServiceForm';
import type { ServiceProfile } from '@/types';

const authTypeBadge = (type: string) => {
  const colors: Record<string, { bg: string; fg: string }> = {
    cookie: { bg: '#dbeafe', fg: '#1d4ed8' },
    bearer: { bg: '#fef3c7', fg: '#92400e' },
    none: { bg: 'var(--bg-secondary)', fg: 'var(--text-muted)' },
  };
  const c = colors[type] || colors.none;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
      backgroundColor: c.bg, color: c.fg,
    }}>
      {type}
    </span>
  );
};

const columns: Column<ServiceProfile>[] = [
  { key: 'displayName', header: 'Name', render: (p) => <strong>{p.displayName}</strong>, width: '160px' },
  { key: 'repo', header: 'Repo', render: (p) => <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{p.repo}</span> },
  { key: 'authType', header: 'Auth', render: (p) => authTypeBadge(p.auth?.type || 'none'), width: '80px' },
  { key: 'previewUrl', header: 'Preview URL', render: (p) => <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{p.previewUrl || '-'}</span> },
  {
    key: 'techStack', header: 'Stack', width: '160px',
    render: (p) => {
      const parts = [p.techStack?.framework, p.techStack?.uiLibrary].filter(Boolean);
      return parts.length > 0 ? (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {parts.map((t) => (
            <span key={t} style={{ padding: '1px 6px', borderRadius: '4px', fontSize: '11px', backgroundColor: '#f0fdf4', color: '#166534' }}>{t}</span>
          ))}
        </div>
      ) : '-';
    },
  },
  {
    key: 'updated', header: 'Updated',
    render: (p) => new Date(p.updatedAt).toLocaleDateString(), width: '100px',
  },
];

export function ServiceList() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const { data: profiles, isLoading } = useQuery({
    queryKey: ['serviceProfiles'],
    queryFn: fetchServiceProfiles,
    refetchInterval: 10_000,
  });

  return (
    <div>
      <section
        style={{
          marginBottom: 40,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 36,
              fontWeight: 800,
              letterSpacing: '-0.025em',
              color: 'var(--text-primary)',
              margin: 0,
              lineHeight: 1.2,
            }}
          >
            Services
          </h1>
          <p style={{ color: 'var(--text-secondary)', maxWidth: 512, margin: '4px 0 0', fontSize: 14 }}>
            Manage registered service profiles, authentication strategies, and tech stacks.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{
            background: 'linear-gradient(135deg, #4a40e0 0%, #9795ff 100%)',
            color: '#fff',
            fontWeight: 700,
            padding: '12px 24px',
            borderRadius: 12,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(74, 64, 224, 0.25)',
            fontSize: 14,
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span>
          Register Service
        </button>
      </section>
      {showForm && <ServiceForm onClose={() => setShowForm(false)} />}
      {isLoading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          data={profiles || []}
          onRowClick={(profile) => navigate(`/ui-orchestrator/services/${profile._id}`)}
          emptyMessage="No services registered yet"
        />
      )}
    </div>
  );
}
